import { useState, useEffect } from "react";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import Layout from "./components/Layout";
import OnboardingWizard from "./components/OnboardingWizard";
import HomePage from "./pages/HomePage";
import VitaminPage from "./pages/VitaminPage";
import LiteraturePage from "./pages/LiteraturePage";
import ReportPage from "./pages/ReportPage";
import RegulatoryPage from "./pages/RegulatoryPage";
import InterpretPage from "./pages/InterpretPage";
import ReportAnalysisPage from "./pages/ReportAnalysisPage";
import AdminPage from "./pages/AdminPage";
import CrmDashboard from "./pages/CrmDashboard";
import { api } from "./api/client";

import AuthPage from "./components/AuthPage";

// Public marketing pages
import MarketingLayout from "./components/marketing/MarketingLayout";
import LandingPage from "./pages/marketing/LandingPage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import PricingPage from "./pages/marketing/PricingPage";
import AboutPage from "./pages/marketing/AboutPage";
import ContactPage from "./pages/marketing/ContactPage";

// ── Onboarding gate: checks if user has completed profile ─────────────────
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { email } = useAuth();
  const [checked, setChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    api.crmGetProfile()
      .then((res) => {
        setNeedsOnboarding(!res.onboarding_complete);
        setChecked(true);
      })
      .catch(() => {
        // 404 = no profile yet → show onboarding
        setNeedsOnboarding(true);
        setChecked(true);
      });
  }, []);

  if (!checked) return null; // brief flash while checking

  if (needsOnboarding) {
    return (
      <OnboardingWizard
        email={email}
        onComplete={() => setNeedsOnboarding(false)}
      />
    );
  }

  return <>{children}</>;
}

// ── Protected app shell ───────────────────────────────────────────────────
function ProtectedApp() {
  return (
    <AuthProvider>
      <OnboardingGate>
        <Layout>
          <Routes>
            <Route path="/"          element={<HomePage />} />
            <Route path="/vitamin"   element={<VitaminPage />} />
            <Route path="/literature"element={<LiteraturePage />} />
            <Route path="/report"    element={<ReportPage />} />
            <Route path="/regulatory"element={<RegulatoryPage />} />
            <Route path="/interpret" element={<InterpretPage />} />
            <Route path="/analyse"   element={<ReportAnalysisPage />} />
            <Route path="/admin"     element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/crm"       element={<AdminRoute><CrmDashboard /></AdminRoute>} />
            <Route path="*"          element={<Navigate to="/app" replace />} />
          </Routes>
        </Layout>
      </OnboardingGate>
    </AuthProvider>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// ── Root app ──────────────────────────────────────────────────────────────
export default function App() {
  return (
    <Routes>
      {/* Public marketing routes */}
      <Route element={<MarketingLayout />}>
        <Route path="/"        element={<LandingPage />} />
        <Route path="/features"element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about"   element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Route>

      {/* Authenticated app */}
      <Route
        path="/app/*"
        element={
          <AuthPage>
            <Authenticator
              hideSignUp={false}
              signUpAttributes={["email", "given_name", "family_name"]}
            >
              <ProtectedApp />
            </Authenticator>
          </AuthPage>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
